import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Image, Film, Music, FolderOpen, ChevronLeft, Check } from 'lucide-react';
import { galleryService } from '../../services/galleryService';
import { useWorkflowStore } from '../../store/workflowStore';
import { callSourcePickerHandler, clearSourcePickerHandler } from '../../utils/sourcePickerBridge';

const TYPE_ICONS = { image: Image, video: Film, audio: Music };

const getAssetName = (asset) => {
  const r2Key = asset.metadata?.r2_key || '';
  const parts = r2Key.split('/');
  return parts[parts.length - 1] || `${asset.asset_type}_${asset.id?.slice(0, 8)}`;
};

export default function GallerySelectModal() {
  const sourcePicker = useWorkflowStore(s => s.sourcePicker);
  const setSourcePickerStep = useWorkflowStore(s => s.setSourcePickerStep);
  const closeSourcePicker = useWorkflowStore(s => s.closeSourcePicker);

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const isOpen = sourcePicker?.step === 'gallery';
  const acceptTypes = sourcePicker?.acceptTypes || [];

  const fetchAssets = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const results = [];
      for (const type of acceptTypes) {
        const res = await galleryService.listAssets({ assetType: type, limit: 100 });
        results.push(...(res.assets || []));
      }
      results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAssets(results);
    } catch (err) {
      console.error('GallerySelectModal fetch error:', err);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen, acceptTypes]);

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
      setSelectedAsset(null);
    }
  }, [isOpen, fetchAssets]);

  const handleClose = useCallback(() => {
    clearSourcePickerHandler();
    closeSourcePicker();
  }, [closeSourcePicker]);

  const handleBack = useCallback(() => {
    setSelectedAsset(null);
    setSourcePickerStep('choose');
  }, [setSourcePickerStep]);

  const handleConfirm = useCallback(() => {
    if (!selectedAsset) return;
    callSourcePickerHandler({
      url: selectedAsset.url,
      fileName: getAssetName(selectedAsset),
      assetType: selectedAsset.asset_type,
      assetId: selectedAsset.id,
    });
    closeSourcePicker();
  }, [selectedAsset, closeSourcePicker]);

  if (!isOpen) return null;

  return createPortal(
    <div className="gallery-select-overlay" onClick={handleClose} data-testid="gallery-select-modal">
      <div className="gallery-select-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="gallery-select-header">
          <button className="gallery-select-back" onClick={handleBack} data-testid="gallery-select-back">
            <ChevronLeft size={18} />
          </button>
          <div className="gallery-select-header-info">
            <FolderOpen size={18} />
            <h3>Seleccionar de galeria</h3>
          </div>
          <button className="gallery-select-close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="gallery-select-body">
          {loading ? (
            <div className="gallery-select-loading">
              <Loader2 size={32} className="animate-spin" />
              <span>Cargando galeria...</span>
            </div>
          ) : assets.length === 0 ? (
            <div className="gallery-select-empty">
              <FolderOpen size={48} />
              <span>No hay archivos disponibles</span>
              <small>Genera contenido primero para verlo aqui</small>
            </div>
          ) : (
            <div className="gallery-select-grid">
              {assets.map(asset => {
                const Icon = TYPE_ICONS[asset.asset_type] || Image;
                const isSelected = selectedAsset?.id === asset.id;
                const name = getAssetName(asset);
                return (
                  <button
                    key={asset.id}
                    className={`gallery-select-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedAsset(asset)}
                    data-testid={`gallery-select-${asset.id}`}
                  >
                    {isSelected && (
                      <div className="gallery-select-check">
                        <Check size={14} />
                      </div>
                    )}
                    {asset.asset_type === 'image' ? (
                      <img src={asset.url} alt={name} loading="lazy" />
                    ) : asset.asset_type === 'video' ? (
                      <div className="gallery-select-media-thumb">
                        {asset.metadata?.thumbnail_url ? (
                          <img src={asset.metadata.thumbnail_url} alt={name} loading="lazy" />
                        ) : (
                          <video src={asset.url} preload="none" muted />
                        )}
                        <Film size={24} className="gallery-select-video-icon" />
                      </div>
                    ) : (
                      <div className="gallery-select-media-thumb audio">
                        <Music size={32} />
                      </div>
                    )}
                    <span className="gallery-select-item-name">
                      {name.length > 22 ? name.slice(0, 19) + '...' : name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="gallery-select-footer">
          <button className="gallery-select-cancel" onClick={handleBack}>Volver</button>
          <button
            className="gallery-select-confirm"
            disabled={!selectedAsset}
            onClick={handleConfirm}
            data-testid="gallery-select-confirm"
          >
            Cargar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
